"use client";

import { useMemo } from "react";
import {
  BigChordNotes,
  type BigChordNote,
} from "@/components/ui/molecules/big-chord-notes";
import {
  BORDER,
  FONT_MONO,
  SURFACE_LO,
  TEXT,
  TEXT_DIM,
  TEXT_MUTE,
  VIOLET,
  VIOLET_BRIGHT,
  VIOLET_GLOW,
  VIOLET_SOFT,
} from "./palette";

type Props = {
  positionId: string;
  noteNames: string[];
  numNotes: number;
  rootPc: number;
  requiredPcs: readonly number[];
  fingeringsRight: number[];
  fingeringsLeft: number[];
  activePcs: ReadonlySet<number>;
  justValidated: boolean;
  allDone: boolean;
  onSwitchToTempo?: () => void;
  maxInvIdx: number;
  prevTitle: string;
  nextTitle: string;
  onPrev: () => void;
  onNext: () => void;
  onListen: () => void;
  onRandom: () => void;
};

export function ChordPracticeGridStage({
  positionId,
  noteNames,
  numNotes,
  rootPc,
  requiredPcs,
  fingeringsRight,
  fingeringsLeft,
  activePcs,
  justValidated,
  allDone,
  onSwitchToTempo,
  maxInvIdx,
  prevTitle,
  nextTitle,
  onPrev,
  onNext,
  onListen,
  onRandom,
}: Props) {
  const hasMultiple = maxInvIdx > 0;

  /*
    Ce que le dessin partagé demande, traduit depuis le modèle de l'exercice.

    Ici « prise » veut dire : la classe de hauteur est enfoncée, quelle que soit
    l'octave — c'est la règle de validation de cet écran, et une main y joue
    l'accord entier, donc les deux doigtés s'allument ensemble. Le parcours
    harmonique répond autrement à la même question, ce qui est précisément
    pourquoi `BigChordNotes` reçoit la réponse au lieu de la calculer.
  */
  const notes = useMemo<BigChordNote[]>(
    () =>
      noteNames.map((name, i) => {
        const played = activePcs.has(requiredPcs[i]);

        return {
          id: `${i}`,
          name,
          root: requiredPcs[i] === rootPc,
          held: played,
          right: { finger: fingeringsRight[i] ?? null, down: played },
          left: { finger: fingeringsLeft[i] ?? null, down: played },
        };
      }),
    [noteNames, requiredPcs, rootPc, activePcs, fingeringsRight, fingeringsLeft],
  );

  return (
    <div
      className="relative z-10 flex flex-1 flex-col items-center justify-center"
      style={
        {
          padding: "20px clamp(24px, 4cqw, 40px)",
          gap: "clamp(12px, 2.5cqh, 28px)",
          containerType: "size",
          minHeight: 0,
          overflow: "hidden",
        } as React.CSSProperties
      }
    >
      <FloatingActions onListen={onListen} onRandom={onRandom} hasMultiple={hasMultiple} />

      <div className="flex items-center" style={{ gap: "clamp(8px, 2cqw, 24px)" }}>
        {hasMultiple ? (
          <NavArrow dir="prev" title={prevTitle} onClick={onPrev} />
        ) : (
          <NavArrowSpacer />
        )}

        <BigChordNotes
          notes={notes}
          resetKey={positionId}
          ariaLabel={`Accord à jouer : ${noteNames.join(", ")}`}
        />

        {hasMultiple ? (
          <NavArrow dir="next" title={nextTitle} onClick={onNext} />
        ) : (
          <NavArrowSpacer />
        )}
      </div>

      <HintPill numNotes={numNotes} justValidated={justValidated} />

      {allDone && onSwitchToTempo && (
        <SwitchToTempoLink onSwitchToTempo={onSwitchToTempo} />
      )}
    </div>
  );
}

type HintPillProps = {
  numNotes: number;
  justValidated: boolean;
};

function HintPill({ numNotes, justValidated }: HintPillProps) {
  return (
    <div
      className="inline-flex items-center"
      style={{
        gap: 10,
        padding: "7px 14px",
        borderRadius: 999,
        background: SURFACE_LO,
        border: `1px solid ${BORDER}`,
        color: TEXT_DIM,
        fontSize: 11,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 5,
          height: 5,
          borderRadius: 99,
          background: justValidated ? VIOLET_BRIGHT : VIOLET,
          animation: "chordPracticePulse 1.6s ease-in-out infinite",
        }}
      />
      {justValidated
        ? "Validé · prochain renversement…"
        : `En attente · joue les ${numNotes} notes`}
    </div>
  );
}

type SwitchToTempoLinkProps = {
  onSwitchToTempo: () => void;
};

function SwitchToTempoLink({ onSwitchToTempo }: SwitchToTempoLinkProps) {
  return (
    <button
      type="button"
      onClick={onSwitchToTempo}
      className="absolute z-20"
      style={{
        bottom: 14,
        right: 18,
        background: "transparent",
        border: "none",
        color: VIOLET_BRIGHT,
        fontFamily: "Inter, sans-serif",
        fontSize: 12,
        fontWeight: 500,
        letterSpacing: "0.01em",
        cursor: "pointer",
        padding: "6px 4px",
        transition: "color 160ms ease, transform 160ms ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = "#fff";
        e.currentTarget.style.transform = "translateX(2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = VIOLET_BRIGHT;
        e.currentTarget.style.transform = "translateX(0)";
      }}
    >
      Passer à l'entraînement en rythme →
    </button>
  );
}

type NavArrowProps = {
  dir: "prev" | "next";
  title: string;
  onClick: () => void;
};

function NavArrow({ dir, title, onClick }: NavArrowProps) {
  const isPrev = dir === "prev";
  const label = isPrev ? `Aller à ${title}` : `Passer à ${title}`;

  const chevron = (
    <span
      aria-hidden="true"
      style={{
        fontSize: 22,
        lineHeight: 1,
        fontWeight: 200,
        fontFamily: "Inter, sans-serif",
        color: "inherit",
        display: "flex",
        alignItems: "center",
        // Compense le décalage visuel des chevrons typographiques qui ne sont
        // pas centrés sur leur boîte de texte.
        marginTop: -2,
      }}
    >
      {isPrev ? "‹" : "›"}
    </span>
  );

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="inline-flex items-center"
      style={{
        gap: 8,
        padding: "8px 10px",
        background: "transparent",
        border: "none",
        color: TEXT_MUTE,
        cursor: "pointer",
        transition: "color 180ms ease",
        fontFamily: FONT_MONO,
        fontSize: 11,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        lineHeight: 1,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = TEXT;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = TEXT_MUTE;
      }}
    >
      {isPrev ? chevron : null}
      <span
        style={{
          color: "inherit",
          maxWidth: "clamp(90px, 14cqw, 180px)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          lineHeight: 1,
        }}
      >
        {title}
      </span>
      {isPrev ? null : chevron}
    </button>
  );
}

function NavArrowSpacer() {
  return <div aria-hidden="true" style={{ width: 80 }} />;
}

type FloatingActionsProps = {
  onListen: () => void;
  onRandom: () => void;
  hasMultiple: boolean;
};

function FloatingActions({
  onListen,
  onRandom,
  hasMultiple,
}: FloatingActionsProps) {
  return (
    <div
      className="absolute z-20 flex items-center"
      style={{ top: 14, right: 18, gap: 8 }}
    >
      {hasMultiple && (
        <FloatingIconButton
          onClick={onRandom}
          ariaLabel="Renversement aléatoire"
        >
          ⤨
        </FloatingIconButton>
      )}
      <FloatingIconButton onClick={onListen} ariaLabel="Écouter l'accord" primary>
        ▶
      </FloatingIconButton>
    </div>
  );
}

type FloatingIconButtonProps = {
  onClick: () => void;
  ariaLabel: string;
  children: React.ReactNode;
  primary?: boolean;
};

function FloatingIconButton({
  onClick,
  ariaLabel,
  children,
  primary,
}: FloatingIconButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      title={ariaLabel}
      style={{
        width: 36,
        height: 36,
        borderRadius: 999,
        background: primary ? VIOLET_SOFT : "rgba(255,255,255,0.04)",
        border: `1px solid ${primary ? VIOLET_GLOW : BORDER}`,
        color: primary ? VIOLET_BRIGHT : TEXT_DIM,
        fontSize: 13,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 1,
        boxShadow: primary ? `0 0 14px ${VIOLET_GLOW}` : "none",
        transition:
          "transform 160ms ease, background-color 160ms ease, color 160ms ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "scale(1.08)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "scale(1)";
      }}
    >
      {children}
    </button>
  );
}
